"use client";

import React, { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getOfficialVideos, getVideoSeries } from "../../redux/main/mainSlice";
import {
  selectOfficialVideos,
  selectVideoSeries,
} from "../../redux/selectors/main/mainSelectors";
import { useRouter } from "next/navigation";
import PortraitVideoCard from "../common/cards/portrait/PortraitVideoCard";
import PlusSymbolCard from "../common/cards/PlusSymbol/PlusSymbolCard";

import { toCleanCdnUrl } from "@/asset-delivery/utils/toCleanCdnUrl";
import type { PrivateCdnTarget } from "@/asset-delivery/components/PrivateCdnImage";

import "./OfficialHome.css";

const OfficialHome = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();

  const officialVideos = useAppSelector(selectOfficialVideos);
  const videoSeries = useAppSelector(selectVideoSeries);

  const OFFICIAL_DETAIL_ENDPOINT = "/official-videos";

  useEffect(() => {
    dispatch(getOfficialVideos());
    dispatch(getVideoSeries());
  }, [dispatch]);

  const introVideos = useMemo(
    () =>
      officialVideos.filter((video) =>
        videoSeries.some((series) => series.intro_video_id === video.id)
      ),
    [officialVideos, videoSeries]
  );

  const makeContentUrl = (video: any) => {
    const params = new URLSearchParams({
      type: "video",
      e: OFFICIAL_DETAIL_ENDPOINT,
      s: "flat",
      a: "auto",
      p: "0",
      t: video.title || "",
    });

    return `/content/${encodeURIComponent(video.slug)}?${params.toString()}`;
  };

  const buildThumbTarget = (video: any): PrivateCdnTarget => ({
    app_label: "main",
    model: "officialvideo",
    object_id: video.id,
  });

  return (
    <div className="official-home-container">
      <div className="official-title-container">
        <h2 className="official-title">
          The Heart of<span className="official-brand-name">TownLIT</span>
        </h2>
        <p className="util-text-hint">
          Faith. Family. Function. These aren&apos;t just words — they define the
          heartbeat of TownLIT. A space where the presence of the Lord isn&apos;t
          just spoken of, but truly dwells among us. Here, you&apos;ll discover the
          vision, the Scriptures that shaped it, and how to walk this journey —
          as a believer, seeker, or fellow builder.
        </p>
      </div>

      <div className="video-grid">
        {introVideos.length > 0 ? (
          <>
            {introVideos.map((video: any) => {
              const thumbnailCdn =
                toCleanCdnUrl(video.thumbnail) ||
                toCleanCdnUrl(video.thumbnail_url) ||
                toCleanCdnUrl(video.thumbnail_signed_url) ||
                undefined;

              return (
                <PortraitVideoCard
                  key={video.id}
                  title={video.title}
                  thumbnail={thumbnailCdn}
                  thumbnailTarget={buildThumbTarget(video)}
                  thumbnailKind="thumbnail"
                  thumbnailFieldName="thumbnail"
                  showPlaylistIcon={!!video.series}
                  iconSize={28}
                  onPlaylistClick={() =>
                    router.push(`/townlit-official/series/${video.series?.slug}`)
                  }
                  onClick={() => router.push(makeContentUrl(video))}
                />
              );
            })}

            <PlusSymbolCard size="md" className="grid-centered-span" />
          </>
        ) : (
          <PlusSymbolCard size="md" className="grid-centered-span" />
        )}
      </div>
    </div>
  );
};

export default OfficialHome;
